import { ReactElement, useContext, useLayoutEffect, useState } from "react"
import { useDebounce } from "react-use"

import Select from "react-select"
import { FormLabel, TextInput } from "@wfp/ui"

import { MappingPriceContext } from "../../../../providers/MappingPricesContext"
import { useGetDCFQuery } from "../../../../services/marketprices/dataCollectionFrequencies"
import { useGetPriceTypeQuery } from "../../../../services/core/priceType"
import { useGetCurrenciesByCountryQuery } from "../../../../services/core/currencies"

import MappingStepHeader from "./MappingStepHeader"

const MappingGlobalFields = (): ReactElement => {
  const { schema, mappingRows, adm0Code, onGlobalEdit } = useContext(MappingPriceContext)

  const [sourceValue, setSourceValue] = useState<string>("")

  const { data: frequencies, isFetching: isLoadingFrequencies } = useGetDCFQuery({})
  const { data: priceTypes, isFetching: isLoadingPriceTypes } = useGetPriceTypeQuery()
  const { data: currencies, isFetching: isLoadingCurrencies } = useGetCurrenciesByCountryQuery(adm0Code ?? 0, {
    skip: !adm0Code,
  })

  useDebounce(
    () => {
      schema.dataSource !== sourceValue && onGlobalEdit("dataSource", sourceValue)
    },
    400,
    [sourceValue],
  )

  useLayoutEffect(() => {
    schema.dataSource && setSourceValue(schema.dataSource)
  }, [schema.dataSource])

  return (
    <div>
      <MappingStepHeader
        title="Global Fields"
        descriptions={[
          "Specify here the values that will be applied to all the rows in the excel file you are uploading.",
          "Leave selections blank if you want to map them to specific columns.",
        ]}
      />

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <FormLabel id="frequency">Collection Frequency</FormLabel>
          <p className="text-sm text-gray-500 mb-1">How often is the data collected</p>
          <Select
            inputId="frequency"
            placeholder="Select frequency"
            isLoading={isLoadingFrequencies}
            options={frequencies ?? []}
            getOptionLabel={option => option.name}
            getOptionValue={option => option.id.toString()}
            value={frequencies?.find(f => f.id === Number(schema.frequency))}
            onChange={e => onGlobalEdit("frequency", e?.id.toString() ?? "")}
            isClearable
            menuPosition="fixed"
          />
        </div>

        {!mappingRows.find(item => item.dataType === "priceType") && (
          <div>
            <FormLabel id="price-type">Price Type</FormLabel>
            <p className="text-sm text-gray-500 mb-1">Global price type for all the rows here</p>
            <Select
              inputId="price-type"
              isLoading={isLoadingPriceTypes}
              placeholder="Select price type"
              options={priceTypes ?? []}
              getOptionLabel={option => option.priceTypeName}
              getOptionValue={option => option.priceTypeID.toString()}
              value={priceTypes?.find(f => f.priceTypeID === Number(schema.priceType))}
              onChange={e => onGlobalEdit("priceType", e?.priceTypeID.toString() ?? "")}
              isClearable
              menuPosition="fixed"
              menuPlacement="auto"
            />
          </div>
        )}

        {!mappingRows.find(item => item.dataType === "currency") && (
          <div>
            <FormLabel id="currency">Currency</FormLabel>
            <p className="text-sm text-gray-500 mb-1">Currency used in the price values</p>
            <Select
              inputId="currency"
              isLoading={isLoadingCurrencies}
              placeholder="Select currency"
              options={currencies ?? []}
              getOptionLabel={option => option.currencyName}
              getOptionValue={option => option.currencyID.toString()}
              value={currencies?.find(f => f.currencyID === Number(schema.currency))}
              defaultValue={currencies?.find(f => f.currencyID === Number(schema.currency))}
              onChange={e => onGlobalEdit("currency", e?.currencyID.toString() ?? "")}
              isClearable
              menuPosition="fixed"
              isDisabled={!!mappingRows.find(item => item.dataType === "currency")}
            />
          </div>
        )}

        {!mappingRows.find(item => item.dataType === "dataSource") && (
          <div>
            <FormLabel id="form-label">Data Source</FormLabel>
            <p className="text-sm text-gray-500 mb-1">Source of the data</p>
            <TextInput
              id="form-label"
              value={sourceValue}
              onChange={e => setSourceValue(e.target.value)}
              className="custom-text-field"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default MappingGlobalFields
